/**
 * Report Generation Context
 *
 * Tracks background report generation jobs and provides notifications
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
  ReportJob,
  startReportGeneration,
  getJobStatus,
  GenerateReportOptions,
} from '../services/reportService';

interface ActiveJob {
  jobId: string;
  institutionId: string;
  institutionName: string;
  period: string;
  status: 'generating' | 'completed' | 'failed';
  reportId?: string;
  error?: string;
  startedAt: Date;
}

interface ReportGenerationContextType {
  activeJobs: ActiveJob[];
  hasActiveJobs: boolean;
  startGeneration: (
    options: GenerateReportOptions & { institutionName: string }
  ) => Promise<string>;
  getJobById: (jobId: string) => ActiveJob | undefined;
  clearCompletedJobs: () => void;
  clearJob: (jobId: string) => void;
}

const ReportGenerationContext = createContext<ReportGenerationContextType | undefined>(undefined);

export function ReportGenerationProvider({ children }: { children: React.ReactNode }) {
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const pollingIntervals = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      pollingIntervals.current.forEach(interval => clearInterval(interval));
    };
  }, []);

  const updateJob = useCallback((jobId: string, updates: Partial<ActiveJob>) => {
    setActiveJobs(prev =>
      prev.map(job => (job.jobId === jobId ? { ...job, ...updates } : job))
    );
  }, []);

  const startPolling = useCallback(
    (jobId: string) => {
      // Poll every 3 seconds
      const interval = setInterval(async () => {
        try {
          const job = await getJobStatus(jobId);

          if (job.status === 'completed') {
            updateJob(jobId, {
              status: 'completed',
              reportId: job.report_id || undefined,
            });
            clearInterval(interval);
            pollingIntervals.current.delete(jobId);

            // Show browser notification if supported
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('Report Ready', {
                body: 'Your report has been generated successfully.',
                icon: '/favicon.ico',
              });
            }
          } else if (job.status === 'failed') {
            updateJob(jobId, {
              status: 'failed',
              error: job.error || 'Generation failed',
            });
            clearInterval(interval);
            pollingIntervals.current.delete(jobId);
          }
        } catch (error) {
          console.error('Error polling job status:', error);
        }
      }, 3000);

      pollingIntervals.current.set(jobId, interval);
    },
    [updateJob]
  );

  const startGeneration = useCallback(
    async (options: GenerateReportOptions & { institutionName: string }): Promise<string> => {
      const { institutionName, ...generateOptions } = options;

      // Start the generation
      const result = await startReportGeneration(generateOptions);
      const jobId = result.job_id;

      // Add to active jobs
      const newJob: ActiveJob = {
        jobId,
        institutionId: options.institution_id,
        institutionName,
        period: options.period,
        status: 'generating',
        startedAt: new Date(),
      };

      setActiveJobs(prev => [...prev, newJob]);

      // Start polling for completion
      startPolling(jobId);

      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }

      return jobId;
    },
    [startPolling]
  );

  const getJobById = useCallback(
    (jobId: string): ActiveJob | undefined => {
      return activeJobs.find(job => job.jobId === jobId);
    },
    [activeJobs]
  );

  const clearCompletedJobs = useCallback(() => {
    setActiveJobs(prev => prev.filter(job => job.status === 'generating'));
  }, []);

  const clearJob = useCallback((jobId: string) => {
    // Stop polling if active
    const interval = pollingIntervals.current.get(jobId);
    if (interval) {
      clearInterval(interval);
      pollingIntervals.current.delete(jobId);
    }

    setActiveJobs(prev => prev.filter(job => job.jobId !== jobId));
  }, []);

  const value: ReportGenerationContextType = {
    activeJobs,
    hasActiveJobs: activeJobs.some(job => job.status === 'generating'),
    startGeneration,
    getJobById,
    clearCompletedJobs,
    clearJob,
  };

  return (
    <ReportGenerationContext.Provider value={value}>
      {children}
    </ReportGenerationContext.Provider>
  );
}

export function useReportGeneration() {
  const context = useContext(ReportGenerationContext);
  if (context === undefined) {
    throw new Error('useReportGeneration must be used within a ReportGenerationProvider');
  }
  return context;
}
